import { useEffect, useId, useRef, useState } from "react";
import { Bug, X, AlertTriangle, Loader2 } from "lucide-react";
import { useCreateBugReport } from "../../hooks/useAPI";
import { useMockData } from "../../config/env";

// Defense-in-depth: string-redact errors so closure refs back to request
// payloads can't leak into devtools. Matches the helper used across the
// other converged pages.
function redactErrorForDev(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'string') return err;
  return String(err);
}

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Severity = "critical" | "major" | "minor" | "cosmetic";
type Category = "ui" | "data" | "integration" | "performance" | "security" | "other";

const SEVERITY_OPTIONS: { value: Severity; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "major",    label: "Major"    },
  { value: "minor",    label: "Minor"    },
  { value: "cosmetic", label: "Cosmetic" },
];

const CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: "ui",          label: "UI"          },
  { value: "data",        label: "Data"        },
  { value: "integration", label: "Integration" },
  { value: "performance", label: "Performance" },
  { value: "security",    label: "Security"    },
  { value: "other",       label: "Other"       },
];

const TITLE_MIN = 5;
const TITLE_MAX = 120;
const DESC_MIN  = 20;
const DESC_MAX  = 2000;

export function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId   = useId();
  const descId    = useId();
  const fieldTitleId = useId();
  const fieldDescId  = useId();

  const [title, setTitle]         = useState("");
  const [description, setDesc]    = useState("");
  const [severity, setSeverity]   = useState<Severity>("minor");
  const [category, setCategory]   = useState<Category>("ui");
  const [errorMsg, setErrorMsg]   = useState<string | null>(null);
  const [submittedAt, setSubmitted] = useState<Date | null>(null);

  const createMutation = useCreateBugReport();
  const isSubmitting   = createMutation.isPending;
  const isMock         = useMockData();

  // ── Reset on close ─────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) return;
    setTitle("");
    setDesc("");
    setSeverity("minor");
    setCategory("ui");
    setErrorMsg(null);
    setSubmitted(null);
  }, [isOpen]);

  // ── ESC + focus trap ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) { e.preventDefault(); return; }
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, isSubmitting, onClose]);

  // ── Move focus into dialog on open, restore on close ───────────────────
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const timer = setTimeout(() => {
      const firstField = dialogRef.current?.querySelector<HTMLInputElement>("input, textarea");
      (firstField ?? dialogRef.current)?.focus();
    }, 0);
    return () => {
      clearTimeout(timer);
      previouslyFocused?.focus();
    };
  }, [isOpen]);

  // ── Body scroll lock — prevents background page-scroll while modal open
  // (WAI-ARIA dialog pattern; matches HowItWorksModal / KeyboardShortcutsModal).
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const titleLen = title.trim().length;
  const descLen  = description.trim().length;
  const titleOk  = titleLen >= TITLE_MIN && titleLen <= TITLE_MAX;
  const descOk   = descLen  >= DESC_MIN  && descLen  <= DESC_MAX;
  const canSubmit = titleOk && descOk && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorMsg(null);
    try {
      // VITE_APP_VERSION is injected at build time when present and falls
      // back to the build mode (production / development) so the founder
      // email always shows *something* meaningful for the app version row.
      const buildVersion =
        (typeof import.meta !== "undefined" &&
          (import.meta.env?.VITE_APP_VERSION as string | undefined)) ||
        (typeof import.meta !== "undefined" && import.meta.env?.MODE) ||
        undefined;

      await createMutation.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        severity,
        category,
        url:        typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined,
        userAgent:  typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        appVersion: buildVersion,
      });
      setSubmitted(new Date());
    } catch (err: unknown) {
      // Cardinal Rule #4 — never echo raw err.message to UI. Backend error
      // strings can leak operational detail (DB connection messages, stack
      // hints, internal field names). Static fallback covers every failure
      // mode; DEV log preserves the redacted diagnostic for engineers
      // without exposing extra context in production audit screenshots.
      if (import.meta.env.DEV) {
        console.warn("[BugReportModal] submit failed:", redactErrorForDev(err));
      }
      setErrorMsg("Could not submit. Please try again.");
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-50 animate-in fade-in-0 duration-200 motion-reduce:animate-none"
        onClick={isSubmitting ? undefined : onClose}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-[560px] outline-none animate-in fade-in-0 zoom-in-95 duration-200 motion-reduce:animate-none"
      >
        {/* max-h + overflow-y on the inner panel so a long description or
            a mobile virtual keyboard doesn't push the form off-screen.
            We cap at 90dvh to honour the dynamic viewport on iOS Safari
            where the URL bar steals fixed-100vh height when scrolled. */}
        <div className="adp-modal max-h-[90dvh] overflow-y-auto">
          {/* ─── Header ───────────────────────────────────────────────── */}
          <div className="flex items-start justify-between pb-4 border-b border-border">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="p-2 border border-border bg-surface-2 flex-shrink-0">
                <Bug className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 id={titleId} className="text-lg font-semibold text-foreground break-words">
                  Report a bug
                </h2>
                <p id={descId} className="text-xs text-muted-foreground mt-0.5 break-words">
                  Goes directly to the founder. Auto-attached: page URL, browser, app version.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="adp-btn p-1 hover:bg-surface-2 border-transparent hover:border-border transition-colors motion-reduce:transition-none flex-shrink-0 ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {submittedAt ? (
            // ─── Success state ────────────────────────────────────────
            <div className="pt-6 pb-2">
              <div className="flex flex-col items-start gap-3">
                <div className="text-sm text-foreground">
                  {isMock
                    ? "Demo mode — your report was simulated. No email was sent."
                    : "Thank you. Your report has been delivered."}
                </div>
                <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider">
                  Submitted · {submittedAt.toISOString()}
                  {isMock && " · DEMO"}
                </div>
                {!isMock && (
                  <p className="text-xs text-muted-foreground">
                    We&apos;ll look at it within one business day. If it&apos;s blocking you, email{" "}
                    <a href="mailto:hello@adjudon.com" className="underline underline-offset-2">
                      hello@adjudon.com
                    </a>
                    .
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 pt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="adp-btn px-4 py-2 bg-surface-1 hover:bg-surface-2 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            // ─── Form state ───────────────────────────────────────────
            <form onSubmit={handleSubmit} className="pt-4 flex flex-col gap-5" noValidate aria-busy={isSubmitting}>
              {/* Title */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <label
                    htmlFor={fieldTitleId}
                    className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider"
                  >
                    Title
                  </label>
                  <span className="text-[10px] text-mono text-muted-foreground tabular-nums">
                    {titleLen}/{TITLE_MAX}
                  </span>
                </div>
                <input
                  id={fieldTitleId}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                  placeholder="One-line summary of what's broken"
                  maxLength={TITLE_MAX}
                  required
                  disabled={isSubmitting}
                  className="w-full bg-surface-1 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand transition-colors motion-reduce:transition-none p-2 rounded-none disabled:opacity-50"
                />
              </div>

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <label
                    htmlFor={fieldDescId}
                    className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider"
                  >
                    Description
                  </label>
                  <span className="text-[10px] text-mono text-muted-foreground tabular-nums">
                    {descLen}/{DESC_MAX}
                  </span>
                </div>
                <textarea
                  id={fieldDescId}
                  value={description}
                  onChange={(e) => setDesc(e.target.value.slice(0, DESC_MAX))}
                  placeholder="What did you do, what did you expect, what happened instead?"
                  maxLength={DESC_MAX}
                  rows={5}
                  required
                  disabled={isSubmitting}
                  className="w-full bg-surface-1 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand transition-colors motion-reduce:transition-none resize-y p-2 rounded-none disabled:opacity-50"
                />
              </div>

              {/* Severity + Category as segmented buttons (matches SDS) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <fieldset className="flex flex-col gap-1.5 min-w-0">
                  <legend className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider">
                    Severity
                  </legend>
                  <div className="grid grid-cols-2 gap-1">
                    {SEVERITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSeverity(opt.value)}
                        disabled={isSubmitting}
                        aria-pressed={severity === opt.value}
                        className={
                          "adp-btn px-2 py-1.5 text-xs transition-colors motion-reduce:transition-none disabled:opacity-50 " +
                          (severity === opt.value
                            ? "bg-surface-2 border-[var(--brand)] text-[var(--brand)]"
                            : "bg-surface-1 hover:bg-surface-2 text-muted-foreground")
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="flex flex-col gap-1.5 min-w-0">
                  <legend className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider">
                    Category
                  </legend>
                  <div className="grid grid-cols-3 gap-1">
                    {CATEGORY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setCategory(opt.value)}
                        disabled={isSubmitting}
                        aria-pressed={category === opt.value}
                        className={
                          "adp-btn px-2 py-1.5 text-xs transition-colors motion-reduce:transition-none disabled:opacity-50 " +
                          (category === opt.value
                            ? "bg-surface-2 border-[var(--brand)] text-[var(--brand)]"
                            : "bg-surface-1 hover:bg-surface-2 text-muted-foreground")
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* Error surface */}
              {errorMsg && (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-2 border border-[var(--badge-critical)] bg-surface-1 text-xs text-foreground"
                >
                  <AlertTriangle className="w-4 h-4 text-[var(--badge-critical)] flex-shrink-0 mt-0.5" />
                  <span className="break-words">{errorMsg}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="adp-btn px-4 py-2 bg-surface-1 hover:bg-surface-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="adp-btn adp-btn-primary px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                  {isSubmitting ? "Sending…" : "Send report"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
